import React, { useState, useEffect } from "react";

import { t } from "i18next";
import { Trans } from "react-i18next";

import BaseModal from "./BaseModal";
import LocalizedLablesCard from "../Cards/LocalizedLablesCard";
import ValidationError from "../Shared/ValidationError";
import { validate } from "../../utils/ValidationHelpers";
import classifiersSchema from "../../schemas/classifiers.schema";
import { isEmpty } from "lodash";

const LocalizedClassifiersModal = ({
    isVisible,
    setIsVisible,
    data,
    setData,
    updateAction,
    addAction,
}) => {
    const [localizations, setLocalizations] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const [error, setError] = useState({});
    const [isFormValid, setIsFormValid] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [apiLanguages, setApiLanguages] = useState({});

    useEffect(() => {
        if (!isVisible) {
            setData({});
            handleLocalizations();
        } else if (isVisible) {
            setIsEdit(!data?.classifierId);
            setData({ ...data, value: getValue() });
        }
    }, [isVisible]);

    useEffect(() => {
        if (!isVisible) return;
        const valueValidation = validateValues();
        validate(
            { ...data, label: localizations.en },
            classifiersSchema.CreateClassifierLabel,
            setIsFormValid,
            setError,
            {},
            valueValidation
        );
    }, [data, localizations]);

    useEffect(() => {
        handleLocalizations();
    }, [data?.localizations]);

    const validateValues = () => {
        const classifierValues = data?.classifierValues;

        if (!classifierValues) return;

        if (classifierValues.includes(parseInt(data.value)))
            return { value: "Value must be unique" };

        return {};
    };

    const handleLocalizations = () => {
        const englishKey = { en: data?.name || "" };
        setLocalizations(
            data.localizations
                ? { ...data.localizations, ...englishKey }
                : englishKey
        );
    };

    const getUpdatePayload = () => {
        const newLocalizations = { ...localizations };
        delete newLocalizations.en;

        return {
            id: data.id,
            label: localizations.en,
            localizations: { ...newLocalizations },
        };
    };

    const getCreatePayload = () => {
        const newLocalizations = { ...localizations };
        delete newLocalizations.en;

        return {
            classifierId: data.classifierId,
            label: localizations.en,
            value: +data.value ?? "",
            localizations: { ...newLocalizations },
        };
    };

    const submitUpdate = () => {
        setIsFormSubmitted(true);
        setIsSubmitting(true);
        if (isFormValid) {
            isEdit
                ? updateAction(getUpdatePayload())
                : addAction(getCreatePayload());
        }
        setIsSubmitting(false);
    };

    const handleOnChange = (e) => {
        setData((prevData) => ({
            ...prevData,
            [e.target.name]: e.target.value,
        }));
    };

    const getValue = () => {
        const classifierValues = data?.classifierValues;
        const IncrementalValue =
            classifierValues &&
            classifierValues[classifierValues?.length - 1] + 1;
        return data?.value ?? IncrementalValue ?? "";
    };

    return (
        <>
            <BaseModal
                isVisible={isVisible}
                setIsVisible={setIsVisible}
                isSubmitting={isSubmitting}
                title={t("editTitle", { entity: t("classifierLabel") })}
                action={{ submitAction: submitUpdate }}
            >
                <div className="input-group">
                    <label className="uppercase">
                        <Trans i18nKey="value">Value</Trans>
                    </label>
                    <input
                        className={`input-element ${isEdit ? "disabled" : ""}`}
                        value={data?.value ?? ""}
                        readOnly={isEdit}
                        type="number"
                        name="value"
                        onChange={handleOnChange}
                        placeholder={t("value")}
                    />
                    <ValidationError
                        errorMessage={isFormSubmitted && error["value"]}
                    />
                </div>

                {!isEmpty(data) && (
                    <LocalizedLablesCard
                        localizations={localizations}
                        setLocalizations={setLocalizations}
                        apiLanguages={apiLanguages}
                        setApiLanguages={setApiLanguages}
                        mainLanguageErrorMessage={
                            isFormSubmitted && error["label"]
                        }
                    />
                )}
            </BaseModal>
        </>
    );
};

export default LocalizedClassifiersModal;
